import type { UserListItem } from "@/models/user.model";
import { DateCell } from "@spz/ui/components/table/cells/date-cell";
import { TextCell } from "@spz/ui/components/table/cells/text-cell";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AccountTypeDropdownCell } from "../components/users/account-type-dropdown-cell";
import { ActionsCell } from "../components/users/actions-cell";
import { StatusDropdownCell } from "../components/users/status-dropdown-cell";

export function useUserListColumns() {
  const { t, i18n } = useTranslation();
  const columnHelper = createColumnHelper<UserListItem>();
  return useMemo(
    () => [
      columnHelper.accessor("firstName", {
        header: t("first_name"),
        enableSorting: true,
        cell: ({ getValue }) => {
          const firstName = getValue() ?? "-";
          return (
            <TextCell value={firstName} showTooltip={false} maxLines={1} />
          );
        },
      }),
      columnHelper.accessor("lastName", {
        header: t("last_name"),
        enableSorting: true,
        cell: ({ getValue }) => {
          const lastName = getValue() ?? "-";
          return <TextCell value={lastName} showTooltip={false} maxLines={1} />;
        },
      }),
      columnHelper.accessor("email", {
        header: t("email"),
        enableSorting: true,
        cell: ({ getValue }) => {
          const email = getValue();
          return (
            <TextCell
              value={email.toString()}
              showTooltip={false}
              maxLines={1}
              className="normal-case"
            />
          );
        },
      }),
      columnHelper.accessor("lastActivity", {
        header: t("last_activity"),
        size: 100,
        enableSorting: true,
        cell: ({ getValue }) => {
          const lastActivity = getValue();
          return <DateCell value={lastActivity} locale={i18n.language} />;
        },
      }),
      columnHelper.accessor("accountType", {
        header: t("account_type"),
        size: 100,
        enableSorting: false,
        cell: ({ row, getValue }) => {
          const accountType = getValue();
          return (
            <AccountTypeDropdownCell
              accountType={accountType}
              user={row.original}
            />
          );
        },
      }),
      columnHelper.accessor("status", {
        header: t("status_user"),
        size: 100,
        enableSorting: false,
        cell: ({ row, getValue }) => {
          const status = getValue();
          return <StatusDropdownCell status={status} user={row.original} />;
        },
      }),
      columnHelper.display({
        id: "actions",
        size: 50,
        header: "",
        cell: ({ row }) => {
          return <ActionsCell row={row} />;
        },
      }),
    ],
    [t, i18n, columnHelper],
  );
}
